import { Injectable } from '@angular/core';
import { AssessmentDefinition, CalculationType, ICFCategory, SideType } from '../models/assessment-definition.model';
import { AssessmentResult, GroupResult, TaskResult, ICFCategoryResult } from '../models/assessment-result.model';

@Injectable({
  providedIn: 'root',
})
export class CalculationService {
  constructor() {}

  /**
   * Berechnet die Ergebnisse für ein Assessment
   * @param definition Die Assessment-Definition
   * @param result Das zu berechnende Ergebnis
   * @returns Das berechnete Ergebnis
   */
  calculateAssessmentResults(definition: AssessmentDefinition, result: AssessmentResult): AssessmentResult {
    // Berechne zuerst die Ergebnisse für jede Aufgabe
    result.groupResults.forEach(groupResult => {
      const groupDef = definition.groups.find(g => g.id === groupResult.groupId);
      if (!groupDef) return;

      groupResult.taskResults.forEach(taskResult => {
        const taskDef = groupDef.tasks.find(t => t.id === taskResult.taskId);
        if (!taskDef) return;

        // Berechne seitenspezifische Prozentwerte, wenn seitenbezogene Werte vorhanden sind
        if (definition.sideType === SideType.Both) {
          if (taskResult.leftValue !== undefined && taskResult.leftValue !== null) {
            taskResult.leftPercentFulfillment = this.calculateTaskPercentFulfillment(
              taskDef,
              taskResult.leftValue as number,
              taskResult.additionalData
            );
          }

          if (taskResult.rightValue !== undefined && taskResult.rightValue !== null) {
            taskResult.rightPercentFulfillment = this.calculateTaskPercentFulfillment(
              taskDef,
              taskResult.rightValue as number,
              taskResult.additionalData
            );
          }

          // Wenn beide Seiten Werte haben, berechne den Durchschnitt für percentFulfillment
          if (taskResult.leftPercentFulfillment !== undefined && taskResult.rightPercentFulfillment !== undefined) {
            taskResult.percentFulfillment =
              (taskResult.leftPercentFulfillment + taskResult.rightPercentFulfillment) / 2;
          }
          // Falls nur eine Seite einen Wert hat, verwende diesen als Gesamtprozentwert
          else if (taskResult.leftPercentFulfillment !== undefined) {
            taskResult.percentFulfillment = taskResult.leftPercentFulfillment;
          } else if (taskResult.rightPercentFulfillment !== undefined) {
            taskResult.percentFulfillment = taskResult.rightPercentFulfillment;
          }
        } else {
          // Einseite Assessments
          if (taskResult.value !== undefined && taskResult.value !== null) {
            taskResult.percentFulfillment = this.calculateTaskPercentFulfillment(
              taskDef,
              taskResult.value as number,
              taskResult.additionalData
            );
          }
        }

        // Berechne ICF-Score basierend auf dem Prozentwert
        if (taskResult.percentFulfillment !== undefined) {
          taskResult.icfScore = this.calculateICFScore(taskResult.percentFulfillment);
        }
      });

      // Dann berechne die Ergebnisse für die Gruppe
      this.calculateGroupResult(definition, groupDef, groupResult);
    });

    // Schließlich berechne das Gesamtergebnis
    this.calculateOverallResult(definition, result);

    return result;
  }

  /**
   * Berechnet das Ergebnis für eine einzelne Aufgabe
   */
  private calculateTaskResult(definition: AssessmentDefinition, groupDef: any, taskResult: TaskResult): void {
    const taskDef = groupDef.tasks.find((t: any) => t.id === taskResult.taskId);
    if (!taskDef || taskResult.value === null) return; // Ignoriere null-Werte

    // Berechnung der prozentualen Erfüllung
    if (taskDef.minValue !== undefined && taskDef.maxValue !== undefined) {
      const range = taskDef.maxValue - taskDef.minValue;
      const value = Number(taskResult.value);

      if (range > 0) {
        taskResult.percentFulfillment = ((value - taskDef.minValue) / range) * 100;
      } else {
        taskResult.percentFulfillment = value === taskDef.maxValue ? 100 : 0;
      }
    }

    // Berechnung des ICF-Scores
    if (taskResult.percentFulfillment !== undefined) {
      taskResult.icfScore = this.mapPercentToICFScore(taskResult.percentFulfillment);
    }
  }

  /**
   * Berechnet das Ergebnis für eine Gruppe
   */
  private calculateGroupResult(definition: AssessmentDefinition, groupDef: any, groupResult: GroupResult): void {
    const validTaskResults = groupResult.taskResults.filter(tr => tr.value !== null); // Ignoriere null-Werte

    if (validTaskResults.length === 0) {
      // Keine gültigen Task-Ergebnisse
      return;
    }

    // Berechne Durchschnitt der prozentualen Erfüllung der Aufgaben
    const tasksWithPercentFulfillment = validTaskResults.filter(tr => tr.percentFulfillment !== undefined);
    if (tasksWithPercentFulfillment.length > 0) {
      const totalPercentFulfillment = tasksWithPercentFulfillment.reduce(
        (sum, tr) => sum + (tr.percentFulfillment || 0),
        0
      );

      groupResult.percentFulfillment = totalPercentFulfillment / tasksWithPercentFulfillment.length;

      // Berechne ICF-Score basierend auf gruppiertem Ergebnis
      groupResult.icfScore = this.mapPercentToICFScore(groupResult.percentFulfillment);
    }

    // Sammle ICF-Kategorien aus allen Tasks dieser Gruppe
    const taskDefs = groupDef.tasks.filter((t: any) => validTaskResults.some(tr => tr.taskId === t.id));

    const icfCategories: any[] = [];
    taskDefs.forEach((taskDef: any) => {
      if (taskDef.icfCategories) {
        taskDef.icfCategories.forEach((cat: any) => {
          if (!icfCategories.some(existing => existing.code === cat.code)) {
            icfCategories.push(cat);
          }
        });
      }
    });

    // Speichere die gesammelten ICF-Kategorien in der Gruppe, wenn vorhanden
    if (icfCategories.length > 0) {
      groupResult.icfCategories = icfCategories;
    } else if (groupDef.icfCategories) {
      // Fallback auf Gruppen-Kategorien, falls vorhanden
      groupResult.icfCategories = groupDef.icfCategories;
    }
  }

  /**
   * Berechnet das Gesamtergebnis für das Assessment
   */
  private calculateOverallResult(definition: AssessmentDefinition, result: AssessmentResult): void {
    // Berechnung entsprechend dem Berechnungstyp
    switch (definition.calculationType) {
      case CalculationType.Sum:
        this.calculateSumResult(definition, result);
        break;
      case CalculationType.NormValueComparison:
        this.calculateNormValueResult(definition, result);
        break;
      case CalculationType.DirectICFMapping:
        this.calculateDirectICFMappingResult(definition, result);
        break;
      case CalculationType.Interpolation:
        this.calculateInterpolationResult(definition, result);
        break;
    }

    // Kopiere ICF-Kategorien aus der Definition
    result.icfCategories = definition.icfCategories;

    // Berechne die Erfüllung und ICF-Scores für jede ICF-Kategorie
    this.calculateICFCategoryResults(definition, result);
  }

  /**
   * Berechnet ein Ergebnis basierend auf Summen-Scores
   */
  private calculateSumResult(definition: AssessmentDefinition, result: AssessmentResult): void {
    let totalPoints = 0;
    let maxPoints = 0;
    let anyValidTask = false;

    result.groupResults.forEach(groupResult => {
      groupResult.taskResults.forEach(taskResult => {
        if (taskResult.value !== null && taskResult.value !== undefined) {
          // Ignoriere null/undefined-Werte
          anyValidTask = true;
          const taskDef = this.findTaskDefinition(definition, taskResult.taskId);
          if (taskDef) {
            totalPoints += Number(taskResult.value);
            maxPoints += taskDef.maxValue !== undefined ? taskDef.maxValue : 0;
          }
        }
      });
    });

    if (anyValidTask && maxPoints > 0) {
      result.percentFulfillment = (totalPoints / maxPoints) * 100;
      result.icfScore = this.mapPercentToICFScore(result.percentFulfillment);
    }
  }

  /**
   * Berechnet ein Ergebnis basierend auf Normwertvergleichen
   */
  private calculateNormValueResult(definition: AssessmentDefinition, result: AssessmentResult): void {
    // Wenn Patientendaten vorhanden sind und Normwerte definiert sind, verwende diese für den Vergleich
    if (result.patientData && definition.normValues && definition.normValues.length > 0) {
      const patientAge = this.calculateAge(result.patientData.birthDate);
      const patientGender = result.patientData.gender || 'unknown';

      // Finde den passenden Normwert für Alter und Geschlecht
      const matchingNormValue = this.findMatchingNormValue(definition.normValues, patientAge, patientGender);

      if (matchingNormValue) {
        // Speichere den verwendeten Normwert im Ergebnis
        result.appliedNormValue = {
          gender: patientGender,
          age: patientAge,
          avgValue: matchingNormValue.avgValue,
          stdDeviation: matchingNormValue.stdDeviation,
        };

        // Berechne die Erfüllung basierend auf dem Normwert
        // Wir nehmen den Durchschnitt der Werte, wenn es mehrere Aufgaben oder beidseitige Messungen gibt
        let totalValue = 0;
        let valueCount = 0;
        let additionalData = {};

        result.groupResults.forEach(groupResult => {
          groupResult.taskResults.forEach(taskResult => {
            if (taskResult.value !== null && taskResult.value !== undefined) {
              totalValue += Number(taskResult.value);
              valueCount++;

              // Sammle zusätzliche Daten für spezielle Berechnungen (wie NHPT)
              if (taskResult.additionalData) {
                additionalData = { ...additionalData, ...taskResult.additionalData };
              }
            }
          });
        });

        if (valueCount > 0) {
          const averageValue = totalValue / valueCount;
          let percentFulfillment = 0;

          // Verwende die richtige Berechnungsmethode für diesen Test
          if (matchingNormValue.calculationMethod) {
            // Wähle die passende Berechnungsmethode basierend auf dem Namen
            if (matchingNormValue.calculationMethod === 'nhptCalculation') {
              percentFulfillment = this.calculateNHPTPercentFulfillment(
                averageValue,
                matchingNormValue,
                additionalData
              );
            } else if (matchingNormValue.calculationMethod === 'standardZScore') {
              percentFulfillment = this.calculateStandardZScore(averageValue, matchingNormValue);
            } else {
              console.warn(`Unbekannte Berechnungsmethode: ${matchingNormValue.calculationMethod}`);
              this.calculateFallbackNormValueResult(result);
              return;
            }
          } else if (matchingNormValue.calculationFormula) {
            // Für Abwärtskompatibilität: Alte Methode mit Formel (NICHT EMPFOHLEN)
            try {
              const calculationFunction = new Function(
                'value',
                'normValue',
                'additionalData',
                matchingNormValue.calculationFormula
              );
              percentFulfillment = calculationFunction(averageValue, matchingNormValue, additionalData);
            } catch (error) {
              console.error('Fehler bei der Berechnung mit der Formel:', error);
              this.calculateFallbackNormValueResult(result);
              return;
            }
          } else {
            // Wenn keine Methode oder Formel definiert ist, verwende Standard-Z-Score
            percentFulfillment = this.calculateStandardZScore(averageValue, matchingNormValue);
          }

          result.percentFulfillment = percentFulfillment;
          result.icfScore = this.calculateICFScore(percentFulfillment);

          // Setze auch die Erfüllung und ICF-Scores für alle Tasks und Gruppen
          this.updateTaskAndGroupResults(result, percentFulfillment);
        } else {
          this.calculateFallbackNormValueResult(result);
        }

        return; // Wir haben die Berechnung durchgeführt, also können wir hier beenden
      }
    }

    // Fallback, wenn keine Normwertberechnung möglich ist
    this.calculateFallbackNormValueResult(result);
  }

  /**
   * Fallback-Berechnung für Normwertvergleiche, wenn keine exakte Berechnung möglich ist
   */
  private calculateFallbackNormValueResult(result: AssessmentResult): void {
    const groupsWithPercentFulfillment = result.groupResults.filter(
      gr => gr.percentFulfillment !== undefined && gr.percentFulfillment !== null
    );

    if (groupsWithPercentFulfillment.length > 0) {
      const totalPercentFulfillment = groupsWithPercentFulfillment.reduce(
        (sum, gr) => sum + (gr.percentFulfillment || 0),
        0
      );

      result.percentFulfillment = totalPercentFulfillment / groupsWithPercentFulfillment.length;
      result.icfScore = this.mapPercentToICFScore(result.percentFulfillment);
    }
  }

  /**
   * Aktualisiert die prozentuale Erfüllung und ICF-Scores für alle Tasks und Gruppen
   */
  private updateTaskAndGroupResults(result: AssessmentResult, percentFulfillment: number): void {
    const icfScore = this.mapPercentToICFScore(percentFulfillment);

    result.groupResults.forEach(groupResult => {
      groupResult.percentFulfillment = percentFulfillment;
      groupResult.icfScore = icfScore;

      groupResult.taskResults.forEach(taskResult => {
        taskResult.percentFulfillment = percentFulfillment;
        taskResult.icfScore = icfScore;
      });
    });
  }

  /**
   * Berechnet das Alter anhand des Geburtsdatums
   */
  private calculateAge(birthDate?: Date): number {
    if (!birthDate) return 0;

    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }

    return age;
  }

  /**
   * Findet den passenden Normwert für Alter und Geschlecht
   */
  private findMatchingNormValue(normValues: any[], age: number, gender: string): any {
    return normValues.find(nv => nv.gender === gender && age >= nv.minAge && age <= nv.maxAge);
  }

  /**
   * Berechnet ein Ergebnis basierend auf direktem ICF-Mapping
   */
  private calculateDirectICFMappingResult(definition: AssessmentDefinition, result: AssessmentResult): void {
    // Implementation für direktes ICF-Mapping
    // Für dieses Beispiel wird ein einfacher Durchschnitt der ICF-Scores der Aufgaben verwendet

    const groupsWithICFScore = result.groupResults.filter(gr => gr.icfScore !== undefined);

    if (groupsWithICFScore.length > 0) {
      const totalICFScore = groupsWithICFScore.reduce((sum, gr) => sum + (gr.icfScore || 0), 0);

      result.icfScore = Math.round(totalICFScore / groupsWithICFScore.length);
      result.percentFulfillment = this.mapICFScoreToPercent(result.icfScore);
    }
  }

  /**
   * Berechnet ein Ergebnis basierend auf Interpolation
   */
  private calculateInterpolationResult(definition: AssessmentDefinition, result: AssessmentResult): void {
    // Implementation für Interpolation
    // Diese Funktion würde eine komplexere Berechnung basierend auf der Definition durchführen
    // Für dieses Beispiel wird ein einfacher Durchschnitt der Aufgabenerfüllung verwendet

    this.calculateNormValueResult(definition, result);
  }

  /**
   * Berechnet die Erfüllung und ICF-Scores für jede ICF-Kategorie
   * Hier werden die Ergebnisse aller Tasks aggregiert, die zu einer bestimmten ICF-Kategorie beitragen
   */
  private calculateICFCategoryResults(definition: AssessmentDefinition, result: AssessmentResult): void {
    // Sammle alle ICF-Kategorien aus dem Assessment und den Tasks
    const allCategories = new Map<string, ICFCategory>();

    // Füge Assessment-ICF-Kategorien hinzu
    if (definition.icfCategories) {
      definition.icfCategories.forEach(cat => {
        allCategories.set(cat.code, cat);
      });
    }

    // Sammle Task-ICF-Kategorien
    definition.groups.forEach(group => {
      group.tasks.forEach(task => {
        if (task.icfCategories) {
          task.icfCategories.forEach(cat => {
            allCategories.set(cat.code, cat);
          });
        }
      });
    });

    if (allCategories.size === 0) return;

    // Für jede ICF-Kategorie, berechne die durchschnittliche Erfüllung und den ICF-Score
    const icfCategoryResults: ICFCategoryResult[] = [];

    allCategories.forEach((category, code) => {
      // Sammle alle TaskResults, die zu dieser ICF-Kategorie beitragen
      const contributingTasks: TaskResult[] = [];

      result.groupResults.forEach(groupResult => {
        const groupDef = definition.groups.find(g => g.id === groupResult.groupId);
        if (!groupDef) return;

        groupResult.taskResults.forEach(taskResult => {
          if (taskResult.value === null) return; // Ignoriere Tasks ohne Wert

          const taskDef = groupDef.tasks.find(t => t.id === taskResult.taskId);
          if (!taskDef || !taskDef.icfCategories) return;

          // Prüfe, ob dieser Task zur aktuellen ICF-Kategorie beiträgt
          if (taskDef.icfCategories.some(cat => cat.code === code)) {
            contributingTasks.push(taskResult);
          }
        });
      });

      // Berechne die durchschnittliche Erfüllung, wenn es beitragende Tasks gibt
      if (contributingTasks.length > 0) {
        const validTasks = contributingTasks.filter(task => task.percentFulfillment !== undefined);
        if (validTasks.length > 0) {
          const totalPercentFulfillment = validTasks.reduce((sum, task) => sum + (task.percentFulfillment || 0), 0);
          const percentFulfillment = totalPercentFulfillment / validTasks.length;
          const icfScore = this.mapPercentToICFScore(percentFulfillment);

          icfCategoryResults.push({
            code,
            description: category.description,
            percentFulfillment,
            icfScore,
          });
        }
      }
    });

    // Sortiere nach ICF-Code
    icfCategoryResults.sort((a, b) => a.code.localeCompare(b.code));

    // Speichere die Ergebnisse im Result-Objekt
    result.icfCategoryResults = icfCategoryResults;
  }

  /**
   * Hilfsfunktion zur Suche eines Task in der Definition
   */
  private findTaskDefinition(definition: AssessmentDefinition, taskId: string): any {
    for (const group of definition.groups) {
      const task = group.tasks.find(t => t.id === taskId);
      if (task) return task;
    }
    return null;
  }

  /**
   * Mappt einen Prozentwert zu einem ICF-Score
   * 0: Schädigung nicht vorhanden (0 - 4 %)
   * 1: Schädigung leicht ausgeprägt (5 - 24 %)
   * 2: Schädigung mäßig ausgeprägt (25 - 49 %)
   * 3: Schädigung erheblich ausgeprägt (50 - 95 %)
   * 4: Schädigung voll ausgeprägt (96 - 100 %)
   *
   * Achtung: Bei den meisten Assessments bedeutet ein höherer Prozentwert
   * eine bessere Funktion, daher muss der Wert umgerechnet werden.
   */
  private mapPercentToICFScore(percent: number): number {
    // Umkehrung des Prozentwertes, da 100% Erfüllung = 0% Schädigung bedeutet
    const percentDamage = 100 - percent;

    if (percentDamage <= 4) return 0;
    if (percentDamage <= 24) return 1;
    if (percentDamage <= 49) return 2;
    if (percentDamage <= 95) return 3;
    return 4;
  }

  /**
   * Mappt einen ICF-Score zu einem Prozentwert der Schädigung (Mittelpunkt des Bereichs)
   */
  private mapICFScoreToPercent(icfScore: number): number {
    // Wir berechnen hier den Prozentsatz der Erfüllung, nicht der Schädigung
    switch (icfScore) {
      case 0:
        return 98; // 100 - 2 (Mittelpunkt von 0-4%)
      case 1:
        return 85.5; // 100 - 14.5 (Mittelpunkt von 5-24%)
      case 2:
        return 63; // 100 - 37 (Mittelpunkt von 25-49%)
      case 3:
        return 27.5; // 100 - 72.5 (Mittelpunkt von 50-95%)
      case 4:
        return 2; // 100 - 98 (Mittelpunkt von 96-100%)
      default:
        return 0;
    }
  }

  /**
   * Berechnet die prozentuale Erfüllung für eine Aufgabe
   */
  private calculateTaskPercentFulfillment(taskDef: any, value: number, additionalData?: any): number {
    if (taskDef.minValue !== undefined && taskDef.maxValue !== undefined) {
      const range = taskDef.maxValue - taskDef.minValue;
      if (range > 0) {
        return ((value - taskDef.minValue) / range) * 100;
      } else {
        return value === taskDef.maxValue ? 100 : 0;
      }
    }
    return 0;
  }

  /**
   * Berechnet den ICF-Score basierend auf dem Prozent der Erfüllung
   * (Public wrapper für mapPercentToICFScore)
   */
  calculateICFScore(percentFulfillment: number): number {
    return this.mapPercentToICFScore(percentFulfillment);
  }

  /**
   * NHPT-spezifische Berechnung des Prozentsatzes der Funktionsfähigkeit
   */
  private calculateNHPTPercentFulfillment(value: number, normValue: any, additionalData: any): number {
    try {
      // Grundlegende Überprüfungen
      if (value === undefined || value === null) {
        console.warn('NHPT Berechnung: Kein Wert vorhanden');
        return 0;
      }
      
      // Sicherstellen, dass normValue.avgValue definiert ist
      if (normValue.avgValue === undefined || normValue.avgValue === null) {
        console.warn('NHPT Berechnung: Kein avgValue im Normwert');
        return 0;
      }

      // Wenn der Test nicht abgeschlossen wurde (60 Sekunden), verwenden wir die eingesteckten/entfernten Stifte
      if (value >= 60) {
        // Überprüfen auf vollständige additionalData
        if (!additionalData) {
          console.warn('NHPT Berechnung: Keine zusätzlichen Daten für unvollständigen Test');
          return 0;
        }

        // Bei unvollständigem Test - Berechnung basierend auf eingesetzten Stiften
        if (additionalData.completionStatus === 'incomplete') {
          const pegsInserted = additionalData.pegsInserted || 0;
          const pegsRemoved = additionalData.pegsRemoved || 0;
          // Max wäre 18 Aktionen (9 Einstecken + 9 Herausnehmen)
          const totalActions = pegsInserted + pegsRemoved;
          return Math.min(100, Math.max(0, (totalActions / 18) * 100)); // Begrenzung auf 0-100%
        }
        return 0;
      } else {
        // Bei vollständigem Test - umgekehrte Proportionalität zur Zeit
        // Je schneller, desto besser (höhere Prozentzahl)
        const maxAcceptableTime = normValue.avgValue * 3;
        
        if (value <= normValue.avgValue) {
          return 100;
        } else if (value >= maxAcceptableTime) {
          return 0;
        } else {
          const percentage = 100 - ((value - normValue.avgValue) / (maxAcceptableTime - normValue.avgValue)) * 100;
          return Math.min(100, Math.max(0, percentage)); // Begrenzung auf 0-100%
        }
      }
    } catch (e) {
      console.error('Fehler in NHPT Berechnungsformel:', e);
      return 0;
    }
  }

  /**
   * Standard Z-Score-Berechnung für Normwertvergleiche
   */
  private calculateStandardZScore(value: number, normValue: any): number {
    const zScore = (value - normValue.avgValue) / normValue.stdDeviation;
    return Math.max(0, Math.min(100, 75 + zScore * 15));
  }
}
